import { useEffect, useState } from 'react'
import { Appearance, type ColorSchemeName } from 'react-native'

/**
 * Maps React Native's nullable ColorSchemeName to our non-nullable ResolvedTheme.
 * Falls back to 'light' when the OS hasn't reported a preference yet.
 */
function toResolvedTheme(scheme: ColorSchemeName | null | undefined): 'light' | 'dark' {
  return scheme === 'dark' ? 'dark' : 'light'
}

/**
 * Returns the current OS color scheme and re-renders when it changes.
 * This is the raw system value — it does NOT apply the user's stored preference.
 *
 * Uses `Appearance.getColorScheme()` for synchronous initial read (no cold-start flash)
 * and `Appearance.addChangeListener` for runtime changes.
 *
 * EN iOS ESTO YA NO ES EL VALOR DEL SISTEMA. `TagoThemeProvider` llama a
 * `Appearance.setColorScheme(resolvedTheme)` para que el chrome nativo --teclado,
 * `Alert`, action sheets, selectores-- siga al interruptor in-app, y ese override
 * es justo lo que `getColorScheme()` devuelve después. O sea que acá se lee, en
 * la práctica, nuestro propio valor.
 *
 * NO IMPORTA HOY, y se comprobó antes de escribirlo: `ThemeMode` es
 * `'light' | 'dark'` --no existe un modo "sistema"-- y `resolveTheme(mode, _systemTheme)`
 * devuelve `mode` ignorando el segundo argumento. Ninguna decisión del producto
 * sale de acá; el valor se guarda para diagnóstico.
 *
 * IMPORTA EL DÍA QUE ALGUIEN AGREGUE UN MODO "SISTEMA": en ese momento esta
 * función estaría preguntándose a sí misma y el modo quedaría clavado. Hay que
 * quitar el override, o leer el tema del SO por una vía que el override no toque.
 */
export function useSystemTheme(): 'light' | 'dark' {
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() =>
    toResolvedTheme(Appearance.getColorScheme()),
  )

  useEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setSystemTheme(toResolvedTheme(colorScheme))
    })
    return () => subscription.remove()
  }, [])

  return systemTheme
}
